import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import MultiSelectDropdownButton from './MultiSelectDropdownButton';
import { useIsInDesign } from '../../FormRenderCtx';

function PermissionsLabelSelectionDropdownButton(props) {
  const { allRowData, onChange, onSelectAll, onUpdateCategories, idMap } = props;
  const { t } = useTranslation(['icp-form-renderer']);
  const isInDesign = useIsInDesign();

  const labelSelectionMap = useMemo(() => {
    const map = {};
    allRowData.forEach(({ permissions, grantedPermissions }) => {
      permissions.forEach(({ name }) => {
        map[name] = map[name] || { total: 0, count: 0 };
        map[name].total += 1;
      });
      grantedPermissions.forEach(({ name }) => {
        if (map[name]) map[name].count += 1;
      });
    });
    return map;
  }, [allRowData]);

  const allPermissionLabelItems = useMemo(() => {
    const labelSet = new Set();
    allRowData.forEach(({ permissions }) => {
      permissions.forEach(({ name }) => labelSet.add(name));
    });

    return [...labelSet]
      .sort((a, b) => a.length - b.length || a.localeCompare(b, navigator.language))
      .map((label) => {
        const item = labelSelectionMap[label] || { count: 0, total: 0 };
        return {
          key: label,
          label,
          checked: item.count === item.total,
          indeterminate: item.count > 0 && item.count < item.total,
        };
      });
  }, [allRowData, labelSelectionMap]);

  const getGrantedPermissionsForSelectedLabelInCategory = useCallback(
    (category, labelKey, checked) => {
      const permOption = category.permissions.find((p) => p.name === labelKey);
      const isSelected = category.grantedPermissions.some((p) => p.name === labelKey);
      let changed = false;
      let grantedPermissions = category.grantedPermissions;

      if (permOption) {
        if (!checked && !isSelected) {
          if (grantedPermissions.length === 0) changed = true;
          grantedPermissions = [...grantedPermissions, permOption];
          idMap.set(category.key, uuidv4());
        } else if (checked && isSelected) {
          if (grantedPermissions.length === 1) changed = true;
          grantedPermissions = grantedPermissions.filter((p) => p.id !== permOption.id);
          idMap.set(category.key, uuidv4());
        }
      }
      return { grantedPermissions, changed };
    },
    [idMap],
  );

  const handleSelectItemForBatchUpdate = useCallback(
    (item) => {
      const checked = labelSelectionMap[item.key].count === labelSelectionMap[item.key].total;
      let categoriesHaveChanged = false;
      const updatedCategories = [];
      const newPermissions = allRowData.flatMap((category) => {
        const { grantedPermissions, changed } = getGrantedPermissionsForSelectedLabelInCategory(
          category,
          item.key,
          checked,
        );
        if (changed) categoriesHaveChanged = true;
        if (grantedPermissions.length > 0) updatedCategories.push(category.key);
        return grantedPermissions;
      });
      if (categoriesHaveChanged) {
        onUpdateCategories(updatedCategories);
      }
      onChange?.(newPermissions);
    },
    [
      allRowData,
      getGrantedPermissionsForSelectedLabelInCategory,
      labelSelectionMap,
      onChange,
      onUpdateCategories,
    ],
  );

  return (
    <MultiSelectDropdownButton
      icon="material:done-all-rounded"
      label={t('permissions.select-all')}
      onClick={onSelectAll}
      items={allPermissionLabelItems}
      onSelectItem={handleSelectItemForBatchUpdate}
      disabled={isInDesign}
    />
  );
}

PermissionsLabelSelectionDropdownButton.propTypes = {
  allRowData: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
  onSelectAll: PropTypes.func,
  onUpdateCategories: PropTypes.func,
  idMap: PropTypes.instanceOf(Map),
};

export default PermissionsLabelSelectionDropdownButton;
